/**
 * Maps human-agent routing API payloads to/from dashboard routing state.
 * Agent ids are numeric in the API; the UI uses string ids for stable React keys.
 */

/** @param {number | string} id */
export function routingAgentIdToUi(id) {
  return String(id);
}

/** @param {string} uiId */
export function routingUiIdToAgentId(uiId) {
  const n = parseInt(String(uiId), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown>} api
 * @param {Array<{ id: number, name: string, email?: string, isOnline?: boolean }>} directoryAgents
 */
export function mapRoutingFromApi(api, directoryAgents) {
  const nameById = new Map(
    (directoryAgents || []).map((a) => [Number(a.id), a.name || a.email || `Agent ${a.id}`])
  );
  const onlineById = new Map(
    (directoryAgents || []).map((a) => [Number(a.id), a.isOnline === true])
  );
  const roundRobinEnabled = Boolean(api.round_robin_enabled);
  const roundRobinCursor = typeof api.round_robin_cursor === "number" ? api.round_robin_cursor : 0;
  const orderRaw = Array.isArray(api.agent_order_ids) ? api.agent_order_ids : [];
  const agentOrderIds = orderRaw.map((x) => String(x));

  const limitsRaw = Array.isArray(api.agent_limits) ? api.agent_limits : [];
  const byAgent = new Map();
  for (const lim of limitsRaw) {
    if (!lim || typeof lim !== "object") continue;
    const aid = routingUiIdToAgentId(/** @type {any} */ (lim).agent_id);
    if (aid == null) continue;
    byAgent.set(aid, {
      minConcurrentChats: Math.max(0, parseInt(String(/** @type {any} */ (lim).min_concurrent_chats ?? 0), 10) || 0),
      maxConcurrentChats: Math.max(1, parseInt(String(/** @type {any} */ (lim).max_concurrent_chats ?? 8), 10) || 8),
    });
  }

  const seen = new Set();
  const agents = [];
  for (const sid of agentOrderIds) {
    const aid = routingUiIdToAgentId(sid);
    if (aid == null || seen.has(aid)) continue;
    seen.add(aid);
    const nm = nameById.get(aid) || `Agent ${aid}`;
    const caps = byAgent.get(aid) || { minConcurrentChats: 0, maxConcurrentChats: 8 };
    agents.push({
      id: routingAgentIdToUi(aid),
      name: nm,
      minConcurrentChats: caps.minConcurrentChats,
      maxConcurrentChats: caps.maxConcurrentChats,
      isOnline: onlineById.get(aid) === true,
    });
  }

  for (const da of directoryAgents || []) {
    const aid = Number(da.id);
    if (!Number.isFinite(aid) || seen.has(aid)) continue;
    seen.add(aid);
    const caps = byAgent.get(aid) || { minConcurrentChats: 0, maxConcurrentChats: 8 };
    agents.push({
      id: routingAgentIdToUi(aid),
      name: da.name || da.email || `Agent ${aid}`,
      minConcurrentChats: caps.minConcurrentChats,
      maxConcurrentChats: caps.maxConcurrentChats,
      isOnline: da.isOnline === true,
    });
  }

  let order = agentOrderIds.length ? [...agentOrderIds] : agents.map((a) => a.id);
  order = order.filter((id) => agents.some((a) => a.id === id));
  if (!order.length && agents.length) {
    order = agents.map((a) => a.id);
  }

  let cursor = roundRobinCursor;
  if (order.length) {
    cursor = Math.max(0, Math.min(cursor, order.length - 1));
  } else {
    cursor = 0;
  }

  return {
    roundRobinEnabled,
    agents,
    agentOrderIds: order,
    roundRobinCursor: cursor,
  };
}

/**
 * @param {{
 *   roundRobinEnabled: boolean,
 *   agents: Array<{ id: string, name: string, minConcurrentChats: number, maxConcurrentChats: number }>,
 *   agentOrderIds: string[],
 *   roundRobinCursor: number,
 * }} state
 */
export function mapRoutingToApi(state) {
  const agent_limits = (state.agents || []).map((a) => {
    const aid = routingUiIdToAgentId(a.id);
    if (aid == null) return null;
    let min = Math.max(0, parseInt(String(a.minConcurrentChats), 10) || 0);
    let max = parseInt(String(a.maxConcurrentChats), 10);
    if (!Number.isFinite(max) || max < 1) max = 8;
    if (max < min) max = min;
    return {
      agent_id: aid,
      min_concurrent_chats: min,
      max_concurrent_chats: max,
    };
  }).filter(Boolean);

  return {
    round_robin_enabled: Boolean(state.roundRobinEnabled),
    agent_order_ids: [...(state.agentOrderIds || [])],
    round_robin_cursor: typeof state.roundRobinCursor === "number" ? state.roundRobinCursor : 0,
    agent_limits,
  };
}
